import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { ByokPanel } from "@/components/ByokPanel";
import { CopyButton } from "@/components/CopyButton";
import { useByok } from "@/hooks/useByok";
import { useSeo } from "@/lib/seo";

export default function AiTitles() {
  useSeo("AI Title Ideas", "Generate 8 click-worthy YouTube titles and SEO descriptions from your hook — bring your own AI key, stored only in your browser.");
  const [params] = useSearchParams();
  const { provider, apiKey, setProvider, setApiKey } = useByok();
  const [hook, setHook] = useState(() => params.get("hook") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return toast.error("Paste your API key first — it never leaves your browser except for this request.");
    if (!hook.trim()) return toast.error("Write a hook or topic first.");
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/titles", { hook, provider, apiKey });
      setResult(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="ai-titles" title="AI Title Ideas" tagline="Paste your own AI key (stored only in your browser), drop in a hook, and get 8 titles plus 2 descriptions." hasResults={!!result}>
      <ByokPanel provider={provider} apiKey={apiKey} setProvider={setProvider} setApiKey={setApiKey} />

      <form onSubmit={generate} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Your hook or topic</label>
        <textarea
          data-testid="ai-hook-input"
          value={hook}
          onChange={(e) => setHook(e.target.value)}
          rows={3}
          placeholder="I spent 30 days waking up at 5am — here's what nobody tells you..."
          className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)] resize-y"
        />
        <button
          data-testid="ai-generate-btn"
          disabled={loading}
          className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
        >
          <Sparkle size={16} weight={loading ? "fill" : "bold"} className={loading ? "animate-pulse" : ""} />
          {loading ? "Generating..." : "Generate titles"}
        </button>
      </form>

      {result && (
        <div className="mt-8 fade-up space-y-4">
          <div className="surface rounded-[20px] p-6 sm:p-8">
            <h2 className="text-lg font-bold mb-4">Titles</h2>
            <div className="space-y-2.5" data-testid="ai-titles-list">
              {result.titles.map((t, i) => (
                <div key={`${i}:${t}`} className="flex items-center justify-between gap-3 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3">
                  <p className="text-sm font-medium">{t}</p>
                  <CopyButton text={t} label="Copy" testId={`copy-title-${i}`} className="!px-3 !py-1.5 shrink-0" />
                </div>
              ))}
            </div>
          </div>
          <div className="surface rounded-[20px] p-6 sm:p-8">
            <h2 className="text-lg font-bold mb-4">Descriptions</h2>
            <div className="space-y-3" data-testid="ai-descriptions-list">
              {result.descriptions.map((d, i) => (
                <div key={`${i}:${d.slice(0, 40)}`} className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4">
                  <p className="text-sm text-[var(--muted)] leading-relaxed">{d}</p>
                  <div className="mt-3"><CopyButton text={d} label="Copy description" testId={`copy-desc-${i}`} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </ToolShell>
  );
}
